import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faCircleArrowUp } from '@fortawesome/pro-regular-svg-icons/faCircleArrowUp';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@rocketlane/ui';
import { useCallback, useState } from 'react';
import { Avatar } from 'components/atoms/avatar';
import { Popover } from 'components/atoms/popover';
import { useDesktopUpdater } from 'components/organisms/desktop-updater';
import { RoutePaths } from 'constant';
import { useAccountSafe } from 'hooks/use-account';
import { useTranslate } from 'hooks/use-translation';
import { useUserSafe } from 'hooks/use-user';
import { fullName } from 'utils/full-name';
import { FooterStack, PresenceDot, PROFILE_POPOVER_OVERLAY_CLASS, ProfileAvatarWrap, ProfileButton, ProfileLogoutLink, ProfileMenuPanel, ProfileMeta, ProfileName, ProfilePopoverOverlayGlobalStyle, ProfileSubtitle, UpdateButton, UpdateCopy, UpdateIconWrap, UpdateSubtitle, UpdateTitle } from './nitro-claw-desktop-sidebar.styles';
export const NitroClawDesktopSidebarFooter = ({ collapsed })=>{
    var _user_firstName, _account_accountName;
    const translate = useTranslate();
    const userProfile = useUserSafe();
    const account = useAccountSafe();
    const { availableVersion, isInstalling, installUpdate } = useDesktopUpdater();
    const [menuOpen, setMenuOpen] = useState(false);
    const hideMenu = useCallback(()=>{
        setMenuOpen(false);
    }, []);
    const updateAria = translate('nitro_claw.sidebar.update.aria', undefined, 'Install desktop update');
    const updateTitle = translate('nitro_claw.sidebar.update.ready', undefined, 'Ready to update!');
    const updateSubtitle = translate('nitro_claw.sidebar.update.click', undefined, 'Click to update');
    const logoutLabel = translate('logout', undefined, 'Logout');
    const user = userProfile === null || userProfile === void 0 ? void 0 : userProfile.user;
    const displayName = user ? ((_user_firstName = user.firstName) === null || _user_firstName === void 0 ? void 0 : _user_firstName.trim()) || fullName(user) : '';
    const accountName = (account === null || account === void 0 ? void 0 : (_account_accountName = account.accountName) === null || _account_accountName === void 0 ? void 0 : _account_accountName.trim()) || '';
    const updateControl = availableVersion ? /*#__PURE__*/ _jsx(MaybeTooltip, {
        tip: collapsed ? updateTitle : undefined,
        children: /*#__PURE__*/ _jsxs(UpdateButton, {
            type: "button",
            $collapsed: collapsed,
            onClick: installUpdate,
            disabled: isInstalling,
            "aria-label": updateAria,
            "data-cy": "nitro-claw-desktop-sidebar.update",
            children: [
                !collapsed ? /*#__PURE__*/ _jsxs(UpdateCopy, {
                    children: [
                        /*#__PURE__*/ _jsx(UpdateTitle, {
                            "data-cy": "nitro-claw-desktop-sidebar.update-title",
                            children: updateTitle
                        }),
                        /*#__PURE__*/ _jsx(UpdateSubtitle, {
                            "data-cy": "nitro-claw-desktop-sidebar.update-subtitle",
                            children: updateSubtitle
                        })
                    ]
                }) : null,
                /*#__PURE__*/ _jsx(UpdateIconWrap, {
                    $collapsed: collapsed,
                    "aria-hidden": true,
                    children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                        icon: faCircleArrowUp
                    })
                })
            ]
        })
    }) : null;
    const profileControl = user ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ProfilePopoverOverlayGlobalStyle, {}),
            /*#__PURE__*/ _jsx(Popover, {
                placement: collapsed ? 'rightTop' : 'topLeft',
                trigger: "click",
                open: menuOpen,
                onOpenChange: setMenuOpen,
                getPopupContainer: ()=>document.body,
                className: PROFILE_POPOVER_OVERLAY_CLASS,
                content: /*#__PURE__*/ _jsx(ProfileMenuPanel, {
                    "data-cy": "nitro-claw-desktop-sidebar.profile-menu",
                    children: /*#__PURE__*/ _jsxs(ProfileLogoutLink, {
                        to: RoutePaths.logout,
                        onClick: hideMenu,
                        "data-cy": "nitro-claw-desktop-sidebar.logout",
                        children: [
                            /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                icon: faTrash,
                                "aria-hidden": true
                            }),
                            logoutLabel
                        ]
                    })
                }),
                children: /*#__PURE__*/ _jsxs(ProfileButton, {
                    type: "button",
                    $collapsed: collapsed,
                    "aria-label": displayName,
                    "aria-expanded": menuOpen,
                    title: collapsed ? displayName : undefined,
                    "data-cy": "nitro-claw-desktop-sidebar.profile",
                    children: [
                        /*#__PURE__*/ _jsxs(ProfileAvatarWrap, {
                            children: [
                                /*#__PURE__*/ _jsx(Avatar, {
                                    src: user.photoUrl,
                                    name: fullName(user),
                                    size: 28
                                }),
                                /*#__PURE__*/ _jsx(PresenceDot, {
                                    "aria-hidden": true,
                                    "data-cy": "nitro-claw-desktop-sidebar.presence"
                                })
                            ]
                        }),
                        !collapsed ? /*#__PURE__*/ _jsxs(ProfileMeta, {
                            children: [
                                /*#__PURE__*/ _jsx(ProfileName, {
                                    title: displayName,
                                    "data-cy": "nitro-claw-desktop-sidebar.profile-name",
                                    children: displayName
                                }),
                                accountName ? /*#__PURE__*/ _jsx(ProfileSubtitle, {
                                    title: accountName,
                                    "data-cy": "nitro-claw-desktop-sidebar.profile-account",
                                    children: accountName
                                }) : null
                            ]
                        }) : null
                    ]
                })
            })
        ]
    }) : null;
    if (!updateControl && !profileControl) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(FooterStack, {
        $collapsed: collapsed,
        "data-cy": "nitro-claw-desktop-sidebar.footer",
        children: [
            updateControl,
            profileControl
        ]
    });
};
const MaybeTooltip = ({ tip, children })=>{
    if (!tip) {
        return children;
    }
    return /*#__PURE__*/ _jsx(Tooltip, {
        content: tip,
        side: "right",
        children: children
    });
};
